import {
  BLOCK_YAW_DEGREES,
  SNAP_STEPS,
  snapYawClass,
  yawClassDegrees,
  type ViewGridState,
} from '../shared/view-grid';
import { subscribeLocale, t } from './i18n';

interface GridEditorOptions {
  getState: () => ViewGridState;
  onChange: (state: Partial<ViewGridState>) => void;
  onClose: () => void;
}

/** Gキーの視点グリッド編集。整数 Y、間隔、6 向き回転。 */
export class GridEditor {
  private readonly root = document.createElement('section');
  private readonly y = this.numberInput('0', '1');
  private readonly yawButtons = new Map<number, HTMLButtonElement>();
  private readonly snapButtons = new Map<number, HTMLButtonElement>();
  private readonly hidden = document.createElement('input');
  private readonly status = document.createElement('p');
  private readonly title = document.createElement('h2');
  private readonly subtitle = document.createElement('p');
  private readonly heightLabel = document.createElement('span');
  private readonly hideLabel = document.createElement('span');
  private readonly yawLabel = document.createElement('span');
  private readonly snapLabel = document.createElement('span');
  private readonly yawGroup = document.createElement('div');
  private readonly snapGroup = document.createElement('div');
  private readonly closeHintText = document.createElement('span');
  private yawClass = 0;
  private snap = 1;
  private statusKind: 'walk' | 'hidden' | 'invalid' = 'walk';

  constructor(private readonly options: GridEditorOptions) {
    this.root.className = 'grid-editor';
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-labelledby', 'grid-editor-title');

    const panel = document.createElement('div');
    panel.className = 'grid-editor-panel';

    const header = document.createElement('header');
    header.className = 'grid-editor-header';
    const titleWrap = document.createElement('div');
    const kicker = document.createElement('p');
    kicker.className = 'grid-editor-kicker';
    kicker.textContent = 'VIEW GRID';
    this.title.id = 'grid-editor-title';
    this.subtitle.className = 'grid-editor-subtitle';
    titleWrap.append(kicker, this.title, this.subtitle);
    header.append(titleWrap, this.closeHint());

    const form = document.createElement('form');
    form.className = 'grid-editor-form';
    form.addEventListener('submit', (event) => event.preventDefault());
    this.hidden.type = 'checkbox';
    this.y.addEventListener('input', () => this.commit());
    this.hidden.addEventListener('change', () => this.commit());
    form.append(this.heightField(), this.snapField(), this.yawField(), this.hideField());

    this.status.className = 'grid-editor-status';

    panel.append(header, form, this.status);
    this.root.append(panel);
    document.body.append(this.root);
    this.applyCopy();
    subscribeLocale(() => this.applyCopy());
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  open(): void {
    if (this.isOpen) return;
    this.syncFromState();
    this.root.hidden = false;
  }

  close(): void {
    if (!this.isOpen) return;
    const focused = this.root.querySelector(':focus');
    if (focused instanceof HTMLElement) focused.blur();
    this.root.hidden = true;
    this.options.onClose();
  }

  applyCopy(): void {
    this.title.textContent = t('gridEditor.title');
    this.subtitle.textContent = t('gridEditor.subtitle');
    this.heightLabel.textContent = t('gridEditor.heightY');
    this.hideLabel.textContent = t('gridEditor.hide');
    this.snapLabel.textContent = t('gridEditor.snap');
    this.yawLabel.textContent = t('gridEditor.rotation');
    this.snapGroup.setAttribute('aria-label', t('gridEditor.snap'));
    this.yawGroup.setAttribute('aria-label', t('gridEditor.rotation'));
    this.closeHintText.textContent = ` ${t('gridEditor.closeHint')}`;
    this.syncStatusText();
  }

  private syncFromState(): void {
    const state = this.options.getState();
    this.y.value = String(state.y);
    this.yawClass = snapYawClass(state.yaw);
    this.snap = state.snap;
    this.hidden.checked = !state.visible;
    this.statusKind = this.hidden.checked ? 'hidden' : 'walk';
    this.syncYawButtons();
    this.syncSnapButtons();
    this.syncStatusText();
  }

  private commit(): void {
    const y = Math.round(Number(this.y.value));
    if (!Number.isFinite(y)) {
      this.statusKind = 'invalid';
      this.syncStatusText();
      return;
    }
    this.y.value = String(y);
    this.statusKind = this.hidden.checked ? 'hidden' : 'walk';
    this.syncStatusText();
    this.options.onChange({
      y,
      yaw: yawClassDegrees(this.yawClass),
      snap: this.snap,
      visible: !this.hidden.checked,
    });
  }

  private syncStatusText(): void {
    this.status.textContent =
      this.statusKind === 'invalid'
        ? t('gridEditor.invalidNumber')
        : this.statusKind === 'hidden'
          ? t('gridEditor.hidden')
          : t('gridEditor.walkOn');
  }

  private heightField(): HTMLLabelElement {
    const field = document.createElement('label');
    field.append(this.heightLabel, this.y);
    return field;
  }

  private hideField(): HTMLLabelElement {
    const field = document.createElement('label');
    field.className = 'grid-editor-check';
    field.append(this.hidden, this.hideLabel);
    return field;
  }

  private snapField(): HTMLDivElement {
    const field = document.createElement('div');
    field.className = 'grid-editor-yaw-field';
    this.snapGroup.className = 'grid-editor-yaw';
    this.snapGroup.setAttribute('role', 'radiogroup');
    for (const snap of SNAP_STEPS) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = String(snap);
      button.setAttribute('role', 'radio');
      button.addEventListener('click', () => {
        this.snap = snap;
        this.syncSnapButtons();
        this.commit();
      });
      this.snapButtons.set(snap, button);
      this.snapGroup.append(button);
    }
    field.append(this.snapLabel, this.snapGroup);
    return field;
  }

  private syncSnapButtons(): void {
    for (const [snap, button] of this.snapButtons) {
      const selected = snap === this.snap;
      button.setAttribute('aria-checked', selected ? 'true' : 'false');
      button.classList.toggle('is-selected', selected);
    }
  }

  private yawField(): HTMLDivElement {
    const field = document.createElement('div');
    field.className = 'grid-editor-yaw-field';
    this.yawGroup.className = 'grid-editor-yaw';
    this.yawGroup.setAttribute('role', 'radiogroup');
    for (const [yawClass, degrees] of BLOCK_YAW_DEGREES.entries()) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = String(degrees);
      button.setAttribute('role', 'radio');
      button.addEventListener('click', () => {
        this.yawClass = yawClass;
        this.syncYawButtons();
        this.commit();
      });
      this.yawButtons.set(yawClass, button);
      this.yawGroup.append(button);
    }
    field.append(this.yawLabel, this.yawGroup);
    return field;
  }

  private syncYawButtons(): void {
    for (const [yawClass, button] of this.yawButtons) {
      const selected = yawClass === this.yawClass;
      button.setAttribute('aria-checked', selected ? 'true' : 'false');
      button.classList.toggle('is-selected', selected);
    }
  }

  private closeHint(): HTMLParagraphElement {
    const hint = document.createElement('p');
    hint.className = 'grid-editor-close-hint';
    const keys = ['G', 'Q', 'Esc'];
    for (const [index, key] of keys.entries()) {
      if (index > 0) hint.append(' / ');
      const kbd = document.createElement('kbd');
      kbd.textContent = key;
      hint.append(kbd);
    }
    hint.append(this.closeHintText);
    return hint;
  }

  private numberInput(value: string, step: string): HTMLInputElement {
    const input = document.createElement('input');
    input.type = 'number';
    input.value = value;
    input.step = step;
    return input;
  }
}
